import { isOnOfflineSite } from '../data/offline-site.model';

/**
 * Assignation groupée de trackers / baromètres à un site, partagée par la fiche site et la page
 * Management d'inside. Les actions en lot passent par `POST /trackers/batch/site` (`TrackerBatchService`) :
 * ces règles filtrent l'UI avant le clic, le backend reste l'autorité (ADR-021).
 */
export type SiteAssignmentEligibility =
  /** Même organisation, sans site. */
  | 'free'
  /** Même organisation, sur un autre site : le backend le déplace (slot LoRa de l'ancien site désactivé). */
  | 'move'
  | 'already'
  | 'no-org'
  | 'other-org'
  | 'site-no-org'
  /** 409 TRACKER_ON_OFFLINE_SITE : ni assignation ni détachement. */
  | 'offline-site';

export interface SiteAssignableDevice {
  id?: string;
  organizationId?: string | null;
  site?: { id?: string | null } | null;
  offlineSite?: unknown;
}

export interface SiteAssignmentTarget {
  id: string;
  organizationId?: string | null;
}

export function siteAssignmentEligibility(
  device: SiteAssignableDevice,
  site: SiteAssignmentTarget
): SiteAssignmentEligibility {
  if (device.site?.id && device.site.id === site.id) return 'already';
  if (isOnOfflineSite(device)) return 'offline-site';
  if (!site.organizationId) return 'site-no-org';
  if (!device.organizationId) return 'no-org';
  if (device.organizationId !== site.organizationId) return 'other-org';
  return device.site?.id ? 'move' : 'free';
}

export function isAssignableToSite(eligibility: SiteAssignmentEligibility): boolean {
  return eligibility === 'free' || eligibility === 'move';
}

export interface SiteAssignmentCandidate<T> {
  device: T;
  /** `free` ou `move` : seuls les appareils réellement assignables sont retenus. */
  eligibility: SiteAssignmentEligibility;
  barometer: boolean;
}

/**
 * Vivier d'une assignation groupée à un site : appareils assignables (`free` puis `move`, ordre
 * d'origine conservé dans chaque groupe), marqués baromètre si leur id est dans `barometerIds`.
 * Exclut ceux déjà sur le site, d'une autre organisation, sans organisation, ou sur un site
 * offline (le backend répondrait 409).
 */
export function siteAssignmentCandidates<T extends SiteAssignableDevice>(
  devices: readonly T[],
  site: SiteAssignmentTarget,
  barometerIds: ReadonlySet<string> = new Set()
): SiteAssignmentCandidate<T>[] {
  const candidates = devices
    .map(device => ({
      device,
      eligibility: siteAssignmentEligibility(device, site),
      barometer: !!device.id && barometerIds.has(device.id),
    }))
    .filter(c => isAssignableToSite(c.eligibility));
  return [...candidates.filter(c => c.eligibility === 'free'), ...candidates.filter(c => c.eligibility === 'move')];
}

/** Organisation commune à tous les appareils, ou null (vide, orgs différentes, ou une absente). */
export function commonOrganizationId(devices: { organizationId?: string | null }[]): string | null {
  const first = devices[0]?.organizationId;
  if (!first) return null;
  return devices.every(d => d.organizationId === first) ? first : null;
}
